import React, { useState } from "react";
import { MessageCircle, X, Mail, HelpCircle, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const FAQS = [
  {
    q: "How do I generate a resume?",
    a: "Upload your LinkedIn PDF (or any resume PDF), paste the job description, choose your settings, and click Generate. Your tailored resume will be ready in under a minute."
  },
  {
    q: "What file format should I upload?",
    a: "We accept PDF files only. To get your LinkedIn PDF: Go to LinkedIn → Your Profile → More → Save to PDF."
  },
  {
    q: "How does the ATS score work?",
    a: "Our AI analyzes keyword matching, formatting, and relevance between your resume and the job description. Higher scores mean better chances of passing Applicant Tracking Systems."
  },
  {
    q: "Can I edit the generated resume?",
    a: "Yes! Click the 'Click to Edit' button on the output panel to make changes directly. Your edits are preserved when you export to PDF."
  },
  {
    q: "How do I export to PDF?",
    a: "After generation, click 'Export PDF' to choose from 6 professional templates. A print-ready PDF opens in a new tab — use Ctrl+P / Cmd+P to save."
  },
  {
    q: "What's included in the free plan?",
    a: "3 resume generations per month, ATS score analysis, keyword matching, and PDF export with all 6 templates."
  },
  {
    q: "How do I upgrade my plan?",
    a: "Click the 'Upgrade' button in the navigation bar. You can pay via Razorpay (cards, UPI, netbanking) or scan the UPI QR code."
  },
  {
    q: "I paid but my plan didn't upgrade",
    a: "For UPI payments, our team verifies within a few hours. If it's been longer, message us on WhatsApp with your transaction ID and we'll sort it out immediately."
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#2a2a38] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 text-left group"
      >
        <span className="text-[#f0ede8] text-xs font-medium pr-4 group-hover:text-[#c8a96e] transition-colors">{q}</span>
        {open ? <ChevronUp size={13} className="text-[#c8a96e] flex-shrink-0" /> : <ChevronDown size={13} className="text-[#8a8799] flex-shrink-0" />}
      </button>
      {open && (
        <p className="text-[#8a8799] text-xs leading-relaxed pb-3 pl-0">{a}</p>
      )}
    </div>
  );
}

export default function SupportButton() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('main'); // 'main' | 'faq'

  const whatsappUrl = "https://wa.me/919500129965?text=Hi%20Resumiq%20team%2C%20I%20need%20help%20with%20";
  const emailUrl = "mailto:contact@resumiq.in?subject=Support%20Request%20-%20Resumiq.in";

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="support-fab"
        onClick={() => { setOpen(!open); setView('main'); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110"
        style={{
          background: open ? '#1c1c26' : '#25D366',
          border: open ? '1px solid #2a2a38' : 'none',
          boxShadow: open ? 'none' : '0 4px 20px rgba(37,211,102,0.4)',
        }}
      >
        {open ? <X size={20} className="text-[#8a8799]" /> : <MessageCircle size={22} className="text-white" fill="white" />}
      </button>

      {/* Support panel */}
      {open && (
        <div
          data-testid="support-panel"
          className="fixed bottom-24 right-6 z-50 w-[340px] max-h-[70vh] bg-[#13131a] border border-[#2a2a38] rounded-xl shadow-2xl overflow-hidden flex flex-col"
          style={{ animation: 'fadeUp 0.2s ease-out' }}
        >
          {/* Header */}
          <div className="p-5 border-b border-[#2a2a38]" style={{ background: 'linear-gradient(135deg, #13131a, #1c1c26)' }}>
            <h3 className="text-[#f0ede8] text-base font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              How can we help?
            </h3>
            <p className="text-[#8a8799] text-xs mt-1">We typically reply within minutes</p>
          </div>

          {view === 'main' ? (
            <div className="p-4 space-y-2.5 flex-1 overflow-y-auto">
              {/* WhatsApp */}
              <a
                data-testid="support-whatsapp"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3.5 p-3.5 rounded-lg border border-[#2a2a38] hover:border-[#25D366]/40 hover:bg-[#25D366]/5 transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-full bg-[#25D366]/15 flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-[#f0ede8] text-sm font-medium group-hover:text-[#25D366] transition-colors">Chat on WhatsApp</p>
                  <p className="text-[#8a8799] text-[0.65rem]">Get instant help from our team</p>
                </div>
                <ExternalLink size={13} className="text-[#8a8799] group-hover:text-[#25D366] transition-colors" />
              </a>

              {/* Email */}
              <a
                data-testid="support-email"
                href={emailUrl}
                className="flex items-center gap-3.5 p-3.5 rounded-lg border border-[#2a2a38] hover:border-[#c8a96e]/40 hover:bg-[#c8a96e]/5 transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-full bg-[#c8a96e]/15 flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-[#c8a96e]" />
                </div>
                <div className="flex-1">
                  <p className="text-[#f0ede8] text-sm font-medium group-hover:text-[#c8a96e] transition-colors">Email us</p>
                  <p className="text-[#8a8799] text-[0.65rem]">contact@resumiq.in</p>
                </div>
                <ExternalLink size={13} className="text-[#8a8799] group-hover:text-[#c8a96e] transition-colors" />
              </a>

              {/* FAQ */}
              <button
                data-testid="support-faq-btn"
                onClick={() => setView('faq')}
                className="w-full flex items-center gap-3.5 p-3.5 rounded-lg border border-[#2a2a38] hover:border-[#60a5fa]/40 hover:bg-[#60a5fa]/5 transition-all duration-200 group text-left"
              >
                <div className="w-10 h-10 rounded-full bg-[#60a5fa]/15 flex items-center justify-center flex-shrink-0">
                  <HelpCircle size={18} className="text-[#60a5fa]" />
                </div>
                <div className="flex-1">
                  <p className="text-[#f0ede8] text-sm font-medium group-hover:text-[#60a5fa] transition-colors">FAQs</p>
                  <p className="text-[#8a8799] text-[0.65rem]">Quick answers to common questions</p>
                </div>
                <ChevronDown size={13} className="text-[#8a8799] -rotate-90 group-hover:text-[#60a5fa] transition-colors" />
              </button>
            </div>
          ) : (
            /* FAQ View */
            <div className="flex-1 overflow-y-auto">
              <button
                data-testid="faq-back-btn"
                onClick={() => setView('main')}
                className="w-full flex items-center gap-2 px-4 py-3 text-xs text-[#c8a96e] hover:bg-[#1c1c26] border-b border-[#2a2a38] transition-colors"
              >
                <ChevronDown size={12} className="rotate-90" /> Back to support
              </button>
              <div className="px-4 py-2">
                {FAQS.map((faq, i) => (
                  <FaqItem key={i} q={faq.q} a={faq.a} />
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-3 border-t border-[#2a2a38] bg-[#0a0a0f]">
            <p className="text-[#8a8799]/60 text-[0.6rem] text-center">Resumiq.in Support</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
